export const roundDecimals = (num) => {
    const value = typeof num === 'number' ? num : parseFloat(num);
    const safeValue = !isNaN(value) ? value : 0;
    return (Math.round(safeValue * 100) / 100).toFixed(2);
};

export const normalizeCompanies = (companies) => {
    if (!companies) return {};

    // Deep clone to ensure we break any frozen references from RTK Query
    let companiesClone;
    try {
        companiesClone = JSON.parse(JSON.stringify(companies));
    } catch (e) {
        console.error('Error cloning companies:', e);
        return {};
    }

    if (!Array.isArray(companiesClone)) {
        // It's already an object map.
        // Ensure items are arrays and structure is correct.
        Object.keys(companiesClone).forEach(key => {
            const group = companiesClone[key];
            if (!group.items && group.orderItems) {
                group.items = group.orderItems;
            }
            if (!group.items) {
                group.items = [];
            }
        });
        return companiesClone;
    }

    // If it's an array, convert to object map
    return companiesClone.reduce((acc, c) => {
        let ruc = 'default';
        if (c.company) {
            if (typeof c.company === 'string') {
                ruc = c.company;
            } else {
                ruc = c.company.ruc || c.company._id || 'default';
            }
        }

        acc[ruc] = {
            companyInfo: c.company || { ruc: 'default', tradeName: 'Comercial Santy' },
            items: c.items || c.orderItems || [],
            itemsPrice: c.itemsPrice || 0,
            shippingPrice: c.shippingPrice || 0,
            taxPrice: c.taxPrice || 0,
            totalPrice: c.totalPrice || 0,
            paymentStatus: c.isPaid ? 'PAID' : (c.paymentStatus || 'PENDING'),
            checkoutLocked: c.checkoutLocked || false,
            ...c,
        };
        
        // Ensure items are mapped correctly if they come as orderItems
        if (!acc[ruc].items.length && c.orderItems) {
            acc[ruc].items = c.orderItems;
        }
        return acc;
    }, {});
};

export const updateCart = (state) => {
    if (!state.companies || Array.isArray(state.companies)) {
        state.companies = normalizeCompanies(state.companies);
    }

    // Recalculate everything for each company
    Object.keys(state.companies).forEach((ruc) => {
        const companyGroup = state.companies[ruc];

        // 1. Items Price
        companyGroup.itemsPrice = roundDecimals(
            companyGroup.items.reduce((sum, item) => {
                if (!item) return sum;
                let basePrice =
                    state.userInfo?.priceAssigned === 'pvp_distribuidor'
                        ? item.priceDist
                        : item.price;

                // Fallback to whichever price is available if the preferred one is missing
                if (basePrice === undefined || basePrice === null) {
                    basePrice = item.price || item.priceDist || 0;
                }

                const discount = item.discount || 0;
                const finalPrice = basePrice * (1 - discount / 100);

                return sum + finalPrice * item.qty;
            }, 0)
        );

        // 2. Shipping Price (per company)
        if (state.shippingAddress?.label) {
            let baseShipping;
            const isLocal =
                state.shippingAddress.city &&
                companyGroup.companyInfo?.city &&
                state.shippingAddress.city.toLowerCase() ===
                    companyGroup.companyInfo.city.toLowerCase();

            if (isLocal && state.shippingAddress.label !== 'pickup') {
                baseShipping = 4.35; // 'P' is 5.35, so 5.35 - 1 = 4.35
                companyGroup.isLocalShipping = true;
            } else {
                companyGroup.isLocalShipping = false;
                switch (state.shippingAddress.label) {
                    case 'P':
                        baseShipping = 5.35;
                        break;
                    case 'S':
                    case 'E':
                    case 'O':
                        baseShipping = 6.1;
                        break;
                    case 'G':
                        baseShipping = 10.3;
                        break;
                    case 'pickup':
                        baseShipping = 0;
                        break;
                    default:
                        baseShipping = 20;
                        break;
                }
            }
            // Multi-company shipping: Each company has its own shipping price
            companyGroup.shippingPrice = roundDecimals(
                baseShipping / (1 + Number(process.env.REACT_APP_TAX_PERCENTAGE))
            );
        } else {
            companyGroup.shippingPrice = '0.00';
            companyGroup.isLocalShipping = false;
        }

        // 3. Tax Price (per company)
        companyGroup.taxPrice = roundDecimals(
            companyGroup.items.reduce((sum, item) => {
                if (!item) return sum;
                let price =
                    state.userInfo?.priceAssigned === 'pvp_distribuidor'
                        ? item.priceDist
                        : item.price;

                if (price === undefined || price === null) {
                    price = item.price || item.priceDist || 0;
                }

                const itemTax = (price * item.qty * (item.taxPercentage || 0)) / 100;
                return sum + Number(itemTax);
            }, 0) +
                Number(companyGroup.shippingPrice) * Number(process.env.REACT_APP_TAX_PERCENTAGE)
        );

        // 4. Total Price (per company)
        companyGroup.totalPrice = roundDecimals(
            Number(companyGroup.itemsPrice) +
                Number(companyGroup.shippingPrice) +
                Number(companyGroup.taxPrice)
        );
    });

    // Calculate global totals
    state.itemsPrice = roundDecimals(
        Object.values(state.companies).reduce((sum, c) => sum + Number(c.itemsPrice), 0)
    );
    state.shippingPrice = roundDecimals(
        Object.values(state.companies).reduce((sum, c) => sum + Number(c.shippingPrice), 0)
    );
    state.taxPrice = roundDecimals(
        Object.values(state.companies).reduce((sum, c) => sum + Number(c.taxPrice), 0)
    );
    state.totalPrice = roundDecimals(
        Object.values(state.companies).reduce((sum, c) => sum + Number(c.totalPrice), 0)
    );

    // Calculate totals for selected company
    if (state.selectedCompanyRuc && state.companies[state.selectedCompanyRuc]) {
        const selected = state.companies[state.selectedCompanyRuc];
        state.selectedItemsPrice = selected.itemsPrice;
        state.selectedShippingPrice = selected.shippingPrice;
        state.selectedTaxPrice = selected.taxPrice;
        state.selectedTotalPrice = selected.totalPrice;
    } else {
        state.selectedItemsPrice = '0.00';
        state.selectedShippingPrice = '0.00';
        state.selectedTaxPrice = '0.00';
        state.selectedTotalPrice = '0.00';
    }

    // Save to localStorage
    const cartJson = JSON.parse(JSON.stringify(state));
    // Preserve selectedCompanyRuc if we're updating from a server order draft
    if (cartJson.selectedCompanyRuc === undefined) {
        const savedCart = localStorage.getItem('cart') ? JSON.parse(localStorage.getItem('cart')) : {};
        if (savedCart.selectedCompanyRuc !== undefined) {
            cartJson.selectedCompanyRuc = savedCart.selectedCompanyRuc;
        }
    }
    localStorage.setItem('cart', JSON.stringify(cartJson));

    return state;
};

export const groupOrderItems = (activeOrder, companiesByRuc) => {
    const companiesState = activeOrder?.companies;
    if (Array.isArray(companiesState)) {
        return companiesState.reduce((acc, c) => {
            // Handle both populated object and string ID/RUC
            let id = c.company?.ruc || c.company?._id || (typeof c.company === 'string' ? c.company : 'default');

            // Resolve company info using companiesByRuc if available, or use the object if populated
            let companyObj;

            if (typeof c.company === 'string') {
                // It's a RUC/ID string
                id = c.company;
                companyObj = companiesByRuc?.[id];
                if (!companyObj) {
                    companyObj = { ruc: id, tradeName: 'Comercial Santy' };
                }
            } else if (c.company && typeof c.company === 'object') {
                // It's an object
                companyObj = c.company;
            } else {
                // Fallback
                companyObj = { ruc: 'default', tradeName: 'Comercial Santy' };
            }

            acc[id] = {
                id: c._id,
                companyInfo: companyObj,
                name: companyObj.tradeName || companyObj.name || 'Comercial Santy',
                items: c.orderItems || c.items || [],
                preDiscountItemsPrice: c.preDiscountItemsPrice || 0,
                discountAmount: c.discountAmount || 0,
                appliedCoupon: c.appliedCoupon || null,
                itemsPrice: c.itemsPrice || 0,
                taxPrice: c.taxPrice || 0,
                shippingPrice: c.shippingPrice || 0,
                totalPrice: c.totalPrice || 0,
                shippingAddress: c.shippingAddress,
                billingAddress: c.billingAddress,
            };
            return acc;
        }, {});
    } else if (companiesState && typeof companiesState === 'object') {
        return companiesState;
    } else if (activeOrder?.orderItems) {
        // Legacy support
        return {
            default: {
                companyInfo: { ruc: 'default', tradeName: 'Comercial Santy' },
                name: 'Comercial Santy',
                items: activeOrder.orderItems,
                itemsPrice: activeOrder.itemsPrice,
                taxPrice: activeOrder.taxPrice,
                shippingPrice: activeOrder.shippingPrice,
                totalPrice: activeOrder.totalPrice,
                paymentStatus: activeOrder.isPaid ? 'PAID' : 'PENDING',
            }
        };
    }
    return {};
};
